import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';

const SKIPPED_CELLS = '.k-hierarchy-cell, .k-group-cell, .k-detail-cell, .k-command-cell, .k-grid-norecords';
// Elements that draw something without text: controls, icons, flags, pills and anything with a tooltip title.
const VISUAL_CONTENT = [
    'input, button, a, select, textarea, svg, img, canvas',
    'fa-icon, flag-icon, app-flag, .fa',
    '[title]:not([title=""])',
    '[class*="icon"], [class*="pill"], [class*="badge"], [class*="flag"]'
].join(', ');
// A cell is only empty when it is built from plain wrappers; any other element may draw its own content.
const WRAPPER_ELEMENTS = new Set(['DIV', 'SPAN', 'P', 'NG-CONTAINER', 'APP-OVERFLOW-TEXT']);
const EMPTY_CLASS = 'cell-empty';

/**
 * Marks empty data cells of every Kendo grid with the `cell-empty` class; the global styles then draw a "-"
 * placeholder in them (and hide the empty wrappers inside, so they do not make the row taller). Cells of
 * columns without a header title (action / icon columns) and cells that hold inputs, buttons, icons, flags
 * or any other element that may draw something are left alone.
 * Opt out for a grid with `data-empty-dash="off"`.
 */
@Directive({
    selector: 'kendo-grid',
    standalone: false
})
export class GridEmptyCellDirective implements AfterViewInit, OnDestroy {
    private observer: MutationObserver;
    private frame = 0;

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private zone: NgZone
    ) {}

    ngAfterViewInit(): void {
        if (typeof MutationObserver === 'undefined') {
            return;
        }
        // Run outside Angular so the observer does not trigger change detection.
        this.zone.runOutsideAngular(() => {
            this.observer = new MutationObserver(() => this.schedule());
            this.observer.observe(this.elementRef.nativeElement, {
                childList: true,
                subtree: true,
                characterData: true
            });
            this.schedule();
        });
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
        cancelAnimationFrame(this.frame);
    }

    private schedule(): void {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.mark());
    }

    private mark(): void {
        const grid = this.elementRef.nativeElement;
        if (grid.getAttribute('data-empty-dash') === 'off') {
            return;
        }

        const titledColumns = new Set<string>();
        grid.querySelectorAll('th[aria-colindex]').forEach((th) => {
            if (th.textContent?.trim()) {
                titledColumns.add(th.getAttribute('aria-colindex'));
            }
        });

        grid.querySelectorAll<HTMLElement>('tbody > tr:not(.k-detail-row):not(.k-grouping-row) > td').forEach((td) => {
            const isEmpty =
                !td.matches(SKIPPED_CELLS) &&
                titledColumns.has(td.getAttribute('aria-colindex')) &&
                !td.textContent?.trim() &&
                !td.querySelector(VISUAL_CONTENT) &&
                Array.from(td.querySelectorAll('*')).every((element) => WRAPPER_ELEMENTS.has(element.tagName));
            if (isEmpty !== td.classList.contains(EMPTY_CLASS)) {
                td.classList.toggle(EMPTY_CLASS, isEmpty);
            }
        });
    }
}
